import { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  MousePointer2,
  Triangle,
  Circle as CircleIcon,
  Minus,
  ArrowRight,
  PenLine,
  Eraser,
  Trash2,
  User,
} from "lucide-react";

const COLORS = ["#ffffff", "#ef4444", "#f59e0b", "#3b82f6", "#0f172a"];

const TOOLS = [
  { id: "select", icon: MousePointer2, label: "Sposta" },
  { id: "player", icon: User, label: "Giocatore" },
  { id: "cone", icon: Triangle, label: "Cinesino" },
  { id: "hurdle", icon: Minus, label: "Ostacolo" },
  { id: "ball", icon: CircleIcon, label: "Pallone" },
  { id: "arrow", icon: ArrowRight, label: "Freccia" },
  { id: "line", icon: PenLine, label: "Linea" },
  { id: "free", icon: PenLine, label: "Libero" },
];

const PIECE_DEFAULTS = {
  player: { type: "player", color: "#3b82f6" },
  cone: { type: "cone", color: "#f59e0b" },
  hurdle: { type: "hurdle", color: "#0f172a" },
  ball: { type: "ball", color: "#ffffff" },
};

function Piece({ piece, onPointerDown }) {
  const { type, x, y, color, label } = piece;
  if (type === "player") {
    return (
      <g transform={`translate(${x}, ${y})`} onPointerDown={onPointerDown} style={{ cursor: "grab" }}>
        <circle r="2.8" fill={color} stroke="white" strokeWidth="0.4" />
        {label && (
          <text textAnchor="middle" dominantBaseline="central" fontSize="2" fontWeight="700" fill="white" style={{ pointerEvents: "none", userSelect: "none" }}>
            {label}
          </text>
        )}
      </g>
    );
  }
  if (type === "cone") {
    return (
      <g transform={`translate(${x}, ${y})`} onPointerDown={onPointerDown} style={{ cursor: "grab" }}>
        <polygon points="0,-2.5 2,1.5 -2,1.5" fill={color} stroke="black" strokeWidth="0.2" />
      </g>
    );
  }
  if (type === "hurdle") {
    return (
      <g transform={`translate(${x}, ${y})`} onPointerDown={onPointerDown} style={{ cursor: "grab" }}>
        <rect x="-3" y="-0.6" width="6" height="1.2" fill={color} stroke="white" strokeWidth="0.2" />
        <rect x="-3" y="0.6" width="0.4" height="1" fill={color} />
        <rect x="2.6" y="0.6" width="0.4" height="1" fill={color} />
      </g>
    );
  }
  if (type === "ball") {
    return (
      <g transform={`translate(${x}, ${y})`} onPointerDown={onPointerDown} style={{ cursor: "grab" }}>
        <circle r="1.5" fill={color} stroke="black" strokeWidth="0.3" />
        <path d="M -1.5 0 L 1.5 0 M 0 -1.5 L 0 1.5" stroke="black" strokeWidth="0.2" />
      </g>
    );
  }
  return null;
}

export default function DrillBoardDialog({ open, onOpenChange, drill, onSave }) {
  const [pieces, setPieces] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState("#ffffff");
  const [drawing, setDrawing] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    if (drill?.diagram) {
      setPieces(drill.diagram.pieces || []);
      setDrawings(drill.diagram.drawings || []);
    } else {
      setPieces([]);
      setDrawings([]);
    }
  }, [drill]);

  const getPoint = (e) => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 60;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(60, y)) };
  };

  const onPointerDown = (e) => {
    const pt = getPoint(e);
    if (tool === "select") return;
    if (["player", "cone", "hurdle", "ball"].includes(tool)) {
      const id = (pieces.reduce((m, p) => Math.max(m, p.id || 0), 0) || 0) + 1;
      const base = PIECE_DEFAULTS[tool];
      const newPiece = {
        id,
        ...base,
        x: pt.x,
        y: pt.y,
        ...(tool === "player" ? { label: String(id), color } : {}),
      };
      setPieces([...pieces, newPiece]);
      return;
    }
    if (tool === "line" || tool === "arrow") {
      setDrawing({ type: tool, points: [pt, pt], color });
    } else if (tool === "free") {
      setDrawing({ type: "free", points: [pt], color });
    }
  };

  const onPointerMove = (e) => {
    if (draggedId !== null && tool === "select") {
      const pt = getPoint(e);
      setPieces((prev) => prev.map((p) => (p.id === draggedId ? { ...p, x: pt.x, y: pt.y } : p)));
      return;
    }
    if (!drawing) return;
    const pt = getPoint(e);
    if (drawing.type === "free") {
      setDrawing({ ...drawing, points: [...drawing.points, pt] });
    } else {
      setDrawing({ ...drawing, points: [drawing.points[0], pt] });
    }
  };

  const onPointerUp = () => {
    if (draggedId !== null) setDraggedId(null);
    if (drawing) {
      setDrawings((d) => [...d, drawing]);
      setDrawing(null);
    }
  };

  const startDrag = (id) => (e) => {
    if (tool !== "select") return;
    e.stopPropagation();
    setDraggedId(id);
  };

  const removeLast = () => setPieces(pieces.slice(0, -1));

  const renderShape = (s, key) => {
    if (s.type === "line" || s.type === "arrow") {
      const [a, b] = s.points;
      return (
        <line
          key={key}
          x1={a.x} y1={a.y} x2={b.x} y2={b.y}
          stroke={s.color} strokeWidth="0.4" strokeLinecap="round"
          markerEnd={s.type === "arrow" ? `url(#drill-arrow-${s.color.replace("#", "")})` : undefined}
        />
      );
    }
    if (s.type === "free") {
      const d = s.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
      return (
        <path
          key={key}
          d={d}
          fill="none"
          stroke={s.color}
          strokeWidth="0.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    }
    return null;
  };

  const arrowColors = [...new Set([...drawings, drawing].filter(Boolean).map((d) => d.color))];

  const save = () => {
    onSave({ pieces, drawings });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lavagna esercizio: {drill?.name || "Nuovo esercizio"}</DialogTitle>
          <DialogDescription className="sr-only">Disegna il diagramma dell'esercizio.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-3 flex-wrap">
          {TOOLS.map((t) => (
            <Button
              key={t.id}
              variant={tool === t.id ? "default" : "outline"}
              onClick={() => setTool(t.id)}
              size="sm"
              className={tool === t.id ? "bg-sky-500 hover:bg-sky-600" : ""}
              data-testid={`drill-tool-${t.id}`}
            >
              <t.icon size={14} className="mr-1" /> {t.label}
            </Button>
          ))}
          <div className="flex gap-1 items-center ml-2">
            <span className="text-xs text-slate-500 mr-1">Colore:</span>
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`h-6 w-6 rounded-full border-2 transition ${
                  color === c ? "border-slate-900 scale-110" : "border-slate-200"
                }`}
                style={{ background: c }}
                aria-label={`Colore ${c}`}
              />
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={removeLast} title="Rimuovi ultimo">
            <Trash2 size={14} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDrawings([])} className="ml-auto">
            <Eraser size={14} className="mr-1" /> Pulisci tracciati
          </Button>
        </div>

        <div className="rounded-2xl overflow-hidden pitch-bg relative aspect-[5/3]">
          <svg
            ref={svgRef}
            viewBox="0 0 100 60"
            preserveAspectRatio="none"
            className="w-full h-full touch-none select-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            data-testid="drill-svg"
          >
            <defs>
              {arrowColors.map((c) => (
                <marker
                  key={c}
                  id={`drill-arrow-${c.replace("#", "")}`}
                  viewBox="0 0 10 10"
                  refX="6" refY="5"
                  markerWidth="4" markerHeight="4"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
                </marker>
              ))}
            </defs>

            {/* Field outline */}
            <g stroke="rgba(255,255,255,0.6)" strokeWidth="0.3" fill="none">
              <rect x="1" y="1" width="98" height="58" />
              <line x1="50" y1="1" x2="50" y2="59" />
            </g>

            {drawings.map((d, i) => renderShape(d, i))}
            {drawing && renderShape(drawing, "live")}

            {pieces.map((p) => (
              <Piece key={p.id} piece={p} onPointerDown={startDrag(p.id)} />
            ))}
          </svg>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={save}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="save-drill-board"
          >
            Salva diagramma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
